<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-wrapper">
        <BackButton
            :label="$t('general.button.back')"
            :to="{ name: 'billingCoachingSelection' }"
            marginBottom="5"/>

        <h6 class="element-header">
          Rechnungslegung
        </h6>
        <div class="element-box">
          <div class="row">
            <div class="col-6">
              <h5 class="form-header mb-4">
                Soziotherapie - Rechnungslegung
              </h5>
            </div>
          </div>

          <Search
              :filters="filters"
              :loading="isSearching"
              :clientDropdown="clientDropdown"
              :officeDropdown="officeDropdown"
              :operationTypeDropdown="operationTypeDropdown"
              @search="previewList"/>

          <p v-if="correctionError" style="color: red; text-align: center; font-size: 16px">
            Sie müssen eine Korrekturnummer eingeben
          </p>

          <form @submit.prevent="" method="post">
            <div class="table-responsive overflow-auto pt-4">
              <table id="dataTable" class="table table-striped">
                <thead>
                <tr>
                  <th class="text-center  pb-2">
                    <label class="custom-checkbox position-relative">
                      <input
                          type="checkbox"
                          v-model="selectAll"
                          @change="toggleAll"
                          class="custom-checkbox position-absolute"/>
                      <div class="custom-check-icon"></div>
                    </label>
                  </th>
                  <th style="text-align: center">Korrektur</th>
                  <th>
                    <a href="#" class="text-right" @click.prevent="sortBy('client')">
                      <span>Kostenträger</span>
                      <i :class="getIconClass('client')"></i>
                    </a>
                  </th>
                  <th>Kundennummer</th>
                  <th>Kundenname</th>
                  <th>Maßnahmentyp</th>
                  <th style="text-align: right">Sitzungen</th>
                  <th style="text-align: right">Rechnungsbetrag</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="coaching in coachingList" :key="coaching.id">
                  <td>
                    <input type="checkbox" v-model="selectedCoachingIds" :value="coaching.id"/>
                  </td>
                  <td style="text-align: center">
                    <input
                        style="margin-right: 15px"
                        type="checkbox"
                        class="correctionCheckbox"
                        :id="coaching.coachingId"
                        :value="coaching.coachingId"
                        @change="(event) => toggleInput(event, coaching.id)"/>
                    <input
                        style="min-width: 200px;"
                        :value="coaching.inputValue"
                        class="form-control"
                        :class="{ 'is-invalid': !coaching.inputDisabled && !coaching.inputValue }"
                        :disabled="coaching.inputDisabled"
                        @input="(event) => handleInput(event, coaching.id)"
                        placeholder="Rechnungsnummer"/>
                  </td>
                  <td>{{ coaching?.client }}</td>
                  <td>{{ coaching?.unemployedInsuranceNumber }}</td>
                  <td>{{ coaching?.unemployedFullName }}</td>
                  <td>{{ coaching?.operationTypeTranslation }}</td>
                  <td style="text-align: right">{{ coaching?.units }}</td>
                  <td style="text-align: right">{{ coaching?.sum }}</td>
                  <td class="text-right pl-5">
                    <button v-if="coaching.operationType === 'sociotherapie_primary'"
                            @click="downloadFile('/billing/socio-therapy-coaching/primary-type-bill-preview',
                       undefined,
                       {...filters, id: coaching?.id, correctionBillNumber: coaching.inputValue})"
                            class="btn btn-primary"><i class="bx bx-pencil"></i> Vorschau
                    </button>
                    <button v-else
                            @click="downloadFile('/billing/socio-therapy-coaching/substitute-type-bill-preview',
                       undefined,
                       {...filters, id: coaching?.id, correctionBillNumber: coaching.inputValue})"
                            class="btn btn-primary"><i class="bx bx-pencil"></i> Vorschau
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-5 mr-1 text-center"
                 v-if="selectedCoachingIds.length > 0 && correctionError === false"
                 @click="createBill">
              <button type="submit" class="btn btn-primary">Rechnung erstellen</button>
            </div>
          </form>
        </div>
      </div>

      <ModalConfirm
          v-model="showCreateBillConfirm"
          confirmationQuestion="Sind Sie sicher, dass Sie eine Rechnung erstellen möchten?"
          :header="approveHeader"
          :isProcessing="isProcessing"
          :onConfirm="confirmCreateBill"/>
    </div>
  </PageTemplate>
</template>

<script setup>
import billingService from '@/api/Billing';
import BackButton from '@/components/button/BackButton.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getClientDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/Billing/Creating/SocioTherapyCoaching/Search.vue';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const selectAll = ref(false);
const clientDropdown = ref([]);
const officeDropdown = ref([]);
const operationTypeDropdown = ref([]);
const coachingList = ref([]);
const approveHeader = ref('');
const isSearching = ref(false);
const isLoaded = ref(false);
const showCreateBillConfirm = ref(false);
const selectedCoachingIds = ref([]);
const correctionBillNumbers = ref([]);
const correctionError = ref(false);
const isProcessing = ref(false);

const filters = ref({
  operationType: 'sociotherapie',
  clientId: '',
  locationId: '',
  officeId: '',
  startDate: '',
  endDate: '',
  signed: false,
});

const toggleInput = (event, coachingId) => {
  const isChecked = event.target.checked;

  if (isChecked) {
    correctionBillNumbers.value = [...correctionBillNumbers.value, {id: coachingId, correctionNumber: ''}];
  } else {
    correctionBillNumbers.value = correctionBillNumbers.value.filter((item) => item.id !== coachingId);
  }

  coachingList.value = coachingList.value.map((coaching) => {
    if (coaching.id === coachingId) {
      return {...coaching, inputDisabled: !isChecked, inputValue: ''};
    } else {
      return coaching;
    }
  });

  if (correctionBillNumbers.value.length > 0) {
    correctionBillNumbers.value.map((correction) => {
      if (correction.correctionNumber === '') {
        correctionError.value = true;
      } else {
        correctionError.value = false;
      }
    });
  } else {
    correctionError.value = false;
  }
};

const handleInput = (event, coachingId) => {
  const value = event.target.value;

  coachingList.value = coachingList.value.map((coaching) => {
    if (coaching.id === coachingId) {
      return {...coaching, inputDisabled: false, inputValue: value};
    } else {
      return coaching;
    }
  });

  correctionBillNumbers.value = correctionBillNumbers.value.map((correction) => {
    if (correction.id === coachingId) {
      if (value.trim() === '') {
        correctionError.value = true;
      } else {
        correctionError.value = false;
      }

      return {...correction, correctionNumber: value};
    } else {
      return correction;
    }
  });
};

async function previewList(passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  try {
    selectAll.value = false;
    selectedCoachingIds.value = [];

    const {data} = await billingService.getSocioTherapyCoachingPreviewList({
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachingList.value = data.data.map((coaching) => ({...coaching, inputDisabled: true, inputValue: ''}));
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getOperationTypeEnum() {
  operationTypeDropdown.value = getEnums?.value?.socioTherapyOperationTypes || [];
}

function toggleAll() {
  if (selectAll.value) {
    selectedCoachingIds.value = coachingList.value.map(coaching => coaching.id);
  } else {
    selectedCoachingIds.value = [];
  }
}

watch(selectedCoachingIds, (newValue) => {
  // If all checkboxes are selected, check the header checkbox
  if (newValue.length > 0) {
    selectAll.value = newValue.length === coachingList.value.length;
  }
});

function createBill() {
  showCreateBillConfirm.value = true;
}

async function confirmCreateBill() {
  try {
    isProcessing.value = true

    await billingService.createSocioTherapyCoachingBill({
      ...filters.value,
      coachingIds: selectedCoachingIds.value,
      correctionBillNumbers: correctionBillNumbers.value,
    });

    showCreateBillConfirm.value = false;
    isProcessing.value = false;

    await router.push({name: 'billingLatestBills'});
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    showCreateBillConfirm.value = false;
    isProcessing.value = false;
    await ErrorToast(error);
  }
}

function getIconClass(key) {
  return getSortIconClass(filters.value, key);
}

async function sortBy(key) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };

  await previewList();
}

onMounted(async () => {
  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getClientDropdown(clientDropdown),
    getOperationTypeEnum(),
  ]);

  isLoaded.value = true;
});
</script>


<style scoped>
.custom-checkbox .custom-check-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background-color: white;
  margin-right: 8px;
}

.custom-checkbox .custom-check-icon::before {
  content: '';
  position: absolute;
  top: 1px;
  left: 4px;
  width: 6px;
  height: 11px;
  border: solid var(--primary);
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-checkbox input[type="checkbox"]:checked + .custom-check-icon::before {
  opacity: 1;
}

.custom-checkbox .custom-check-icon:hover {
  border-color: var(--primary);
}
</style>